import { useMemo, useState } from 'react';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { buildMemberPracticeRecommendations } from '../domain/raidPracticeRecommendations.js';
import { useRaidPracticeRecommendationCatalog } from '../hooks/useRaidPracticeRecommendationCatalog.js';
import { RaidPracticeRecommendationDeck } from './RaidPracticeRecommendationDeck.jsx';

function numberValue(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function formatEok(value) {
  return `${numberValue(value).toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억`;
}

function memberLevel(member = {}) {
  return numberValue(member.synchroLevel ?? member.synchro_level ?? member.level, 0);
}

function memberKey(member = {}) {
  return String(member.openid ?? member.memberOpenid ?? member.member_openid ?? member.nickname ?? '').trim();
}

function bossLabel(bossMap, attack) {
  return bossDisplayName(bossMap.get(Number(attack.bossNo)) || attack);
}

function bestDifferenceLabel(bestTotalDamageEok, totalDamageEok) {
  const difference = Math.max(0, numberValue(bestTotalDamageEok) - numberValue(totalDamageEok));
  return difference > 0 ? `BEST 대비 -${formatEok(difference)}` : 'BEST 동일';
}

function RecommendationAttackCell({ attack, attackIndex, bossMap }) {
  return (
    <div className="practice-rec-table-attack">
      <div className="practice-rec-table-attack-meta">
        <span>{attackIndex + 1}타</span>
        <strong>{bossLabel(bossMap, attack)}</strong>
        <em>{formatEok(attack.damageEok)}</em>
      </div>
      <small>모의전 {attack.slotNo}</small>
      <RaidPracticeRecommendationDeck deck={attack.deck} />
    </div>
  );
}

function RecommendationCombinationCells({
  combination,
  bossMap,
  totalLabel,
}) {
  return (
    <>
      {combination.attacks.map((attack, attackIndex) => (
        <RecommendationAttackCell
          key={attack.id}
          attack={attack}
          attackIndex={attackIndex}
          bossMap={bossMap}
        />
      ))}
      <div className="practice-rec-table-total">
        <small>{totalLabel}</small>
        <strong>{formatEok(combination.totalDamageEok)}</strong>
      </div>
    </>
  );
}

export function RaidPracticeRecommendationBoard({
  members = [],
  practiceRecords = [],
  bosses = [],
  selectedBossNo = null,
  mobile = false,
}) {
  const [expandedMembers, setExpandedMembers] = useState(() => new Set());
  const {
    catalog,
    loading: catalogLoading,
    errorMessage: catalogErrorMessage,
  } = useRaidPracticeRecommendationCatalog();
  const bossMap = useMemo(
    () => new Map((Array.isArray(bosses) ? bosses : []).map((boss) => [Number(boss.bossNo), boss])),
    [bosses],
  );
  const recommendations = useMemo(() => {
    if (!catalog) return [];
    return buildMemberPracticeRecommendations({
      members,
      practiceRecords,
      limit: 5,
      bossNo: selectedBossNo,
      catalog,
    });
  }, [catalog, members, practiceRecords, selectedBossNo]);

  const toggleAlternatives = (key, enabled) => {
    if (!enabled) return;
    setExpandedMembers((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <section className={`practice-rec-table-board ${mobile ? 'mobile' : 'desktop'}`}>
      <header className="practice-rec-table-guide">
        <strong>중복 없는 3타 추천</strong>
        <p>멤버를 누르면 대체 조합을 펼치며, 대체 조합은 BEST 대비 딜 차이를 함께 표시합니다.</p>
      </header>

      <div className="practice-rec-table-shell">
        <div className="practice-rec-table-body">
          {catalogLoading ? (
            <div className="practice-rec-table-no-results">니케 중복을 확인하는 중입니다.</div>
          ) : catalogErrorMessage ? (
            <div className="practice-rec-table-no-results">{catalogErrorMessage}</div>
          ) : (
            <>
              {recommendations.map((result) => {
                const key = memberKey(result.member);
                const level = memberLevel(result.member);
                const best = result.combinations[0] || null;
                const alternatives = result.combinations.slice(1);
                const expanded = expandedMembers.has(key);
                const hasAlternatives = alternatives.length > 0;

                return (
                  <section className={`practice-rec-table-member ${expanded ? 'expanded' : ''}`} key={key}>
                    <button
                      type="button"
                      className={`practice-rec-member-toggle ${hasAlternatives ? 'clickable' : ''}`}
                      onClick={() => toggleAlternatives(key, hasAlternatives)}
                      aria-expanded={hasAlternatives ? expanded : undefined}
                      disabled={!hasAlternatives}
                    >
                      <span className="practice-rec-member-identity">
                        <small>{level ? `Lv.${level}` : 'MEMBER'}</small>
                        <strong>{result.member.nickname || '닉네임 확인 불가'}</strong>
                        <em>완성 덱 {result.candidateCount}개</em>
                      </span>
                      <span className="practice-rec-member-summary">
                        <strong>{best ? `최고 ${formatEok(best.totalDamageEok)}` : '추천 없음'}</strong>
                        <small>{hasAlternatives ? `대체 ${alternatives.length}개 ${expanded ? '접기' : '보기'}` : '대체 조합 없음'}</small>
                        {hasAlternatives ? <b aria-hidden="true">{expanded ? '▴' : '▾'}</b> : null}
                      </span>
                    </button>

                    {best ? (
                      <>
                        <div className="practice-rec-member-column-head" aria-hidden="true">
                          <span>1타</span><span>2타</span><span>3타</span><span>합계</span>
                        </div>
                        <div className="practice-rec-member-combination best">
                          <RecommendationCombinationCells
                            combination={best}
                            bossMap={bossMap}
                            totalLabel="BEST"
                          />
                        </div>

                        {expanded ? (
                          <div className="practice-rec-table-alternatives">
                            {alternatives.map((combination, index) => (
                              <div className="practice-rec-member-combination alternative" key={combination.key}>
                                <RecommendationCombinationCells
                                  combination={combination}
                                  bossMap={bossMap}
                                  totalLabel={`#${index + 2} · ${bestDifferenceLabel(best.totalDamageEok, combination.totalDamageEok)}`}
                                />
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <div className="practice-rec-table-empty-result">
                        <strong>추천 가능한 3타 조합이 없습니다.</strong>
                        <span>{result.candidateCount < 3 ? '완성된 5인 모의전 덱이 3개 이상 필요합니다.' : '완성 덱은 있지만 세 덱 사이에 중복되는 니케가 있습니다.'}</span>
                      </div>
                    )}
                  </section>
                );
              })}

              {!recommendations.length ? <div className="practice-rec-table-no-results">조건에 맞는 멤버가 없습니다.</div> : null}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
